import { useGameStore } from '../stores/gameStore';

function Results() {
  const roundHistory = useGameStore((s) => s.roundHistory);
  const players = useGameStore((s) => s.players);
  const playerStats = useGameStore((s) => s.playerStats);
  const currentRound = useGameStore((s) => s.currentRound);
  const config = useGameStore((s) => s.config);

  const latestRound = roundHistory[roundHistory.length - 1];

  const getPlayerName = (playerId: string) => {
    const player = players.find((p) => p.id === playerId);
    return player?.name || 'Unknown';
  };

  // Sort players by score for scoreboard
  const scoreboard = [...players]
    .filter((p) => p.connectionType !== 'display')
    .map((p) => ({
      ...p,
      score: playerStats[p.id]?.score || 0,
      boringWins: playerStats[p.id]?.boringWins || 0,
      excitingWins: playerStats[p.id]?.excitingWins || 0,
      boringStreak: playerStats[p.id]?.boringStreak || 0,
    }))
    .sort((a, b) => b.score - a.score);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-8">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <div className="badge badge-primary badge-outline mb-2">Round {currentRound} Results</div>
        </div>

        {latestRound && (
          <>
            {/* Boring Winner */}
            <div className="card bg-warning/10 border border-warning/30">
              <div className="card-body py-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{'\u{1F634}'}</span>
                  <h3 className="text-lg font-bold text-warning">Most Boring</h3>
                  <span className="text-sm text-base-content/40 ml-auto">
                    {latestRound.boringWinner.votes} vote{latestRound.boringWinner.votes !== 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-base italic">"{latestRound.boringWinner.dare}"</p>
                <p className="text-sm mt-2">
                  <span className="font-bold">{getPlayerName(latestRound.boringWinner.playerId)}</span>
                  {' '}must do this dare!
                </p>
                {latestRound.boringWinner.mustTakeShot && (
                  <div className="mt-2 py-2 px-3 bg-error/20 border border-error/40 rounded-lg text-center">
                    <span className="text-lg">{'\u{1F943}'}</span>
                    <span className="font-bold text-error ml-2">TAKE A SHOT!</span>
                    <p className="text-xs text-error/70 mt-1">
                      {getPlayerName(latestRound.boringWinner.playerId)} has been voted most boring 3+ rounds in a row!
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Exciting Winner */}
            <div className="card bg-success/10 border border-success/30">
              <div className="card-body py-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{'\u{1F525}'}</span>
                  <h3 className="text-lg font-bold text-success">Most Exciting</h3>
                  <span className="text-sm text-base-content/40 ml-auto">
                    {latestRound.excitingWinner.votes} vote{latestRound.excitingWinner.votes !== 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-base italic">"{latestRound.excitingWinner.dare}"</p>
                <p className="text-sm mt-2">
                  <span className="font-bold">{getPlayerName(latestRound.excitingWinner.playerId)}</span>
                  {' '}gets +1 point!
                </p>
              </div>
            </div>
          </>
        )}

        {/* Scoreboard */}
        <div className="card bg-base-200">
          <div className="card-body py-4">
            <h3 className="text-sm font-semibold text-base-content/60 uppercase tracking-wide mb-2">
              Scoreboard
            </h3>
            <div className="space-y-2">
              {scoreboard.map((p, i) => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 py-1"
                >
                  <span className="text-base-content/40 text-sm w-5 text-right font-mono">
                    {i + 1}.
                  </span>
                  <span className="flex-1 font-medium">
                    {p.name}
                    {p.boringStreak >= 2 && (
                      <span className="ml-1 badge badge-warning badge-xs">
                        {'\u{1F634}'} {p.boringStreak}
                      </span>
                    )}
                    {p.boringStreak >= 3 && (
                      <span className="ml-1 badge badge-error badge-xs">
                        {'\u{1F943}'}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-2">
                    <progress
                      className="progress progress-primary w-16"
                      value={p.score}
                      max={config.targetPoints}
                    />
                    <span className="font-bold text-primary min-w-[2rem] text-right">
                      {p.score}/{config.targetPoints}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="text-center text-base-content/40 text-sm animate-pulse">
          Next round starting soon...
        </p>
      </div>
    </div>
  );
}

export default Results;
